import moment from 'moment';
import { Moment } from 'moment';
import { CleanType } from '../objects/enum/cleanType';
import { Constants } from '../const/constants';
import { CostType } from '../objects/order/costType';
import { NgForm, FormGroup, MaxLengthValidator } from '@angular/forms';

export class CommonUtils {
  static formatDateTime(d: string) {
    if (d && d !== '') {
      return moment(d).format(Constants.DATE_TIME_FORMAT);
    }
    return '';
  }

  static combineAndFormatDateTimes(a: string, b: string) {
    if (a && a !== '' && b && b !== '') {
      const am = moment(a);
      const bm = moment(b);

      if (
        am.year() === bm.year() &&
        am.month() === bm.month() &&
        am.day() === bm.day()
      ) {
        return (
          am.format(Constants.DATE_TIME_FORMAT) +
          bm.format(Constants.TIME_FORMAT_HOURADDITION)
        );
      } else if (am.year() === bm.year() && am.month() === bm.month()) {
        return (
          am.format(Constants.DATE_DAY_ONLY) + bm.format(Constants.DATE_FORMAT)
        );
      } else {
        return `${moment(a).format(Constants.DATE_FORMAT)} - ${moment(b).format(
          Constants.DATE_FORMAT,
        )}`;
      }
    }
  }

  static formatPlace(p: string) {
    if (p && p != '') {
      const tokens = p.split(',', 3);
      const city = tokens[1];
      return city;
    }
    return '';
  }

  static formatDate(d: string) {
    if (d && d !== '') {
      return moment(d).format(Constants.DATE_FORMAT);
    }
    return '';
  }

  static formatTime(d: string) {
    if (d && d !== '') {
      return moment(d).format(Constants.TIME_FORMAT);
    }
    return '';
  }

  static formatLoadingTime(min: string, max: string) {
    if (min !== '' && max !== '') {
      var minTime = moment(min);
      var maxTime = moment(max);
      if (
        minTime.hour() == maxTime.hour() &&
        minTime.minute() == maxTime.minute()
      )
        return minTime.format(Constants.TIME_FORMAT);
      else
        return (
          minTime.format(Constants.TIME_FORMAT) +
          '-' +
          maxTime.format(Constants.TIME_FORMAT)
        );
    }
    return '';
  }

  static wait(ms: number) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  static nullOrEmpty(str: string) {
    return !str || str.length === 0;
  }

  static hoursSince(m: Moment): number {
    return moment().diff(m, 'hours');
  }

  static formatNumber(n: any) {
    return n.replace(',', '.');
  }

  static UUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
      const random = (Math.random() * 16) | 0; // Nachkommastellen abschneiden

      // Bei x Random 0-15 (0-F), bei y Random 0-3 + 8 = 8-11 (8-b) gemäss RFC 4122
      const value = char === 'x' ? random : (random % 4) + 8;
      return value.toString(16); // Hexadezimales Zeichen zurückgeben
    });
  }

  static deepCopy(obj: any) {
    let copy;

    // Handle the 3 simple types, and null or undefined
    if (null === obj || 'object' !== typeof obj) {
      return obj;
    }

    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.deepCopy(obj[i]);
      }
      return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
      copy = {};
      for (const attr in obj) {
        if (obj.hasOwnProperty(attr)) {
          copy[attr] = this.deepCopy(obj[attr]);
        }
      }
      return copy;
    }

    throw new Error("Unable to copy obj! Its type isn't supported.");
  }

  static getCleanType(
    loadWithoutCleaning: boolean,
    cleanedOnArrival: boolean,
    cleanAtLoadLocation: boolean,
  ): number {
    if (loadWithoutCleaning) {
      return CleanType.LoadWithoutCleaning;
    } else if (cleanedOnArrival) {
      return CleanType.CleanedOnArrival;
    } else if (cleanAtLoadLocation) {
      return CleanType.CleanAtLoadLocation;
    } else {
      return 0;
    }
  }

  static momentIsMidnight(time: Moment): boolean {
    if (typeof time === 'string') {
      return;
    }
    return time.hours() === 0 && time.minutes() === 0 && time.seconds() === 0;
  }

  static sanitizeDate(date: Moment | any) {
    return moment(date).seconds(0).milliseconds(0);
  }

  static setMinDateValue(selectedDate: Moment) {
    if (!!selectedDate) {
      const momentDate = moment(selectedDate);
      return this.toDate(momentDate.add('minutes', CostType.ONE_MINUTE));
    }
    return null;
  }

  static setMaxDateValue(selectedDate: Moment) {
    if (!!selectedDate) {
      const momentDate = moment(selectedDate);
      return this.toDate(momentDate.subtract('minutes', CostType.ONE_MINUTE));
    }
    return null;
  }

  static toDate(date: Moment): Date {
    const dt = this.sanitizeDate(moment(date));
    const asDate = dt.toDate();
    return asDate;
  }

  static getExtension(fileName: string): string {
    return fileName.substr(fileName.lastIndexOf('.') + 1);
  }

  static getFileName(file: string): string {
    return file.substr(0, file.lastIndexOf('.'));
  }

  static markAsDirty(...args: any[]): void {
    for (const entry of args) {
      entry.control.markAsDirty();
    }
  }

  static markAsTouched(...args: any[]): void {
    for (const entry of args) {
      entry.control.markAsTouched();
    }
  }

  static markFormAsTouched(form): void {
    Object.keys(form.controls).forEach(key => {
      form.controls[key].markAsTouched();
    });
  }

  static updateValueAndValidity(...args: any[]): void {
    for (const entry of args) {
      if (!!entry) {
        entry.control.updateValueAndValidity();
      }
    }
  }

  static formatGridTextMobile(text: string): string {
    if (text) return text.length > 20 ? `${text.substr(0, 20)}...` : text;
    return text;
  }

  static formatGridTextTablet(text: string): string {
    if (text) return text.length > 30 ? `${text.substr(0, 30)}...` : text;
    return text;
  }

  static formatGridTextDesktop(text: string): string {
    if (text) return text.length > 50 ? `${text.substr(0, 50)}...` : text;
    return text;
  }

  static formatGridTextDesktopDynamic(
    text: string,
    breakLength: number,
  ): string {
    if (text)
      return text.length > breakLength
        ? `${text.substring(0, breakLength)}...`
        : text;
    return text;
  }

  static formatTextPortraitMobile(text: string): string {
    if (text) return text.length > 30 ? `${text.substr(0, 30)}...` : text;
    return text;
  }
  static formatTextLandscapeMobile(text: string): string {
    if (text) return text.length > 30 ? `${text.substr(0, 30)}...` : text;
    return text;
  }

  static formatTextLandscapeTablet(text: string): string {
    if (text) return text.length > 50 ? `${text.substr(0, 50)}...` : text;
    return text;
  }
  static formatTextPortraitTablet(text: string): string {
    if (text) return text.length > 50 ? `${text.substr(0, 50)}...` : text;
    return text;
  }

  static formatTextDesktop(text: string): string {
    if (text) return text.length > 50 ? `${text.substr(0, 50)}...` : text;
    return text;
  }
}
